import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { settingsActions, FACTORY_USER_DEFAULTS } from "../slices/settings";
import { getErrorMessage } from "../../utils/errorUtils";
import { BranchSettingsRequestDto } from "../../shared/dtos/branchSettings/BranchSettingsRequestDto";
import { BranchSettingsResponseDto } from "../../shared/dtos/branchSettings/BranchSettingsResponseDto";
import { UserDefaults } from "../../shared/interfaces/UserDefaults";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

import { receiptActions } from "../slices/receipt";
import { invoiceActions } from "../slices/invoice";
import { purchaseOrderActions } from "../slices/purchaseOrder";
import { quotationActions } from "../slices/quotation";
// --- Database (API) Actions ---

export const getBranchSettings = () => async (dispatch: AppDispatch) => {
  dispatch(settingsActions.setLoadingBranchSettings());
  try {
    const { data } = await axiosInstance.get<
      ApiResponseDto<BranchSettingsResponseDto>
    >("/branch-settings", {
      headers: { requiresAuth: true },
    });
    if (data.status === ApiResponseStatus.SUCCESS && data.data) {
      dispatch(settingsActions.setBranchSettings(data.data));
    }
  } catch (error) {
    dispatch(settingsActions.setErrorBranchSettings(getErrorMessage(error)));
  }
};

export const updateBranchSettings =
  (payload: BranchSettingsRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(settingsActions.setLoadingUpdateBranchSettings());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<BranchSettingsResponseDto>
      >("/branch-settings", payload, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Settings updated successfully!");
        dispatch(settingsActions.updateBranchSettingsSuccess(data.data));
        dispatch(
          settingsActions.setServerStatus({
            operation: "update-branch-settings",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(settingsActions.setErrorUpdateBranchSettings(message));
    }
  };

// --- Local (Browser Storage) Actions ---

export const loadLocalSettings = () => (dispatch: AppDispatch) => {
  dispatch(settingsActions.setLoadingLocalSettings(true));
  try {
    const localDefaults = localStorage.getItem("user_defaults");

    if (localDefaults)
      dispatch(settingsActions.setUserDefaults(JSON.parse(localDefaults)));
    else dispatch(settingsActions.setUserDefaults(FACTORY_USER_DEFAULTS));
  } catch (error) {
    console.error("Failed to load local settings", error);
  } finally {
    dispatch(settingsActions.setLoadingLocalSettings(false));
  }
};

export const saveUserDefaults =
  (defaults: UserDefaults) => (dispatch: AppDispatch) => {
    dispatch(settingsActions.setUserDefaults(defaults));
    localStorage.setItem("user_defaults", JSON.stringify(defaults));
    toast.success("Operational defaults saved locally.");
  };

export const resetAllLocalSettings = () => (dispatch: AppDispatch) => {
  dispatch(settingsActions.resetSettings());
  localStorage.removeItem("user_defaults");
  toast.success("Local preferences have been reset to defaults.");
};

// --- Cache Management ---

export const clearAppCache = () => (dispatch: AppDispatch) => {
  dispatch(receiptActions.clearPosCart());
  dispatch(invoiceActions.clearInvCart());
  dispatch(purchaseOrderActions.clearPoCart());
  dispatch(quotationActions.clearQuoteCart());

  localStorage.clear();
  sessionStorage.clear();
  toast.success("Application cache cleared. Reloading...");
  setTimeout(() => {
    window.location.href = "/";
  }, 1000);
};

export const resetSettingsServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(settingsActions.setServerStatus(null));
};
